import { defineComponent, computed, ref, openBlock, createElementBlock, createElementVNode, createBlock, unref, createCommentVNode, withCtx, createVNode, mergeProps, normalizeClass, Fragment, renderList, resolveDynamicComponent, withKeys, withModifiers, createTextVNode, toDisplayString } from "vue";
import { CdrButton, IconArrowLeft, IconArrowRight, IconCaretDown } from "@rei/cedar";
import GenericMedia from "../../GenericMedia/GenericMedia.mjs";
import MediaCenterHeroImage from "../MediaCenterHeroImage/MediaCenterHeroImage.mjs";
import UgcImage from "../../UgcImage/UgcImage.mjs";
import { EImageType } from "../../../types/shared.mjs";
import ElevatedSpecs from "../../ElevatedSpecs/ElevatedSpecs3.mjs";
import StaffPickBadge from "../../StaffPickBadge/StaffPickBadge2.mjs";
import { EStaffTestimonialVariants } from "../../StaffTestimonial/StaffTestimonial.mjs";
/* empty css                                        */
import { EElevatedSpecsCase } from "../../ElevatedSpecs/ElevatedSpecs.mjs";
const _hoisted_1 = {
  class: "media-center-grid"
};
const _hoisted_2 = {
  class: "media-center-grid--mobile"
};
const _hoisted_3 = {
  class: "media-center-grid__staff-pick-container--mobile"
};
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "MediaCenterGrid",
  props: {
    heroImageProps: {},
    images: {},
    showArrows: {
      type: Boolean,
      default: false
    },
    showRewardsTag: {
      type: Boolean,
      default: false
    },
    showXlBreakpoint: {
      type: Boolean,
      default: false
    },
    showMoreImagesButton: {
      type: Boolean
    },
    elevatedSpecs: {},
    elevatedSpecsDesktopExperience: {
      default: EElevatedSpecsCase.CONTROL
    },
    elevatedSpecsMobileExperience: {
      default: EElevatedSpecsCase.CONTROL
    },
    staffTestimonialProps: {
      default: void 0
    },
    staffTestimonialExperience: {
      default: EStaffTestimonialVariants.CONTROL
    }
  },
  emits: ["imageSelected", "heroClick", "ugcImageClick", "metricsClick", "prevImage", "nextImage", "ugcImageViewerSelect"],
  setup(__props, {
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;
    const elevatedSpecsDesktopType = computed(() => props.elevatedSpecsDesktopExperience);
    const elevatedSpecsMobileType = computed(() => props.elevatedSpecsMobileExperience);
    const showElevatedSpecsMobile = computed(() => props.elevatedSpecs && elevatedSpecsMobileType.value === EElevatedSpecsCase.MEDIA_CENTER_MOBILE);
    const isElevatedSpecsDesktopOverlay = computed(() => elevatedSpecsDesktopType.value === EElevatedSpecsCase.MEDIA_CENTER_OVERLAY);
    const imageRefs = ref([]);
    const buttonClicked = ref(false);
    const showImagesButton = computed(() => {
      if (buttonClicked.value) return false;
      return props.showMoreImagesButton;
    });
    const filteredImages = computed(() => {
      var _a;
      const desktopImages = props.images.filter((img) => img.type !== EImageType.TESTIMONIAL);
      const isMediaCenterSection = elevatedSpecsDesktopType.value === EElevatedSpecsCase.MEDIA_CENTER_SECTION;
      if (isMediaCenterSection && ((_a = desktopImages[1]) == null ? void 0 : _a.type) !== EImageType.ELEVATED_SPEC) {
        desktopImages.splice(1, 0, {
          type: EImageType.ELEVATED_SPEC
        });
      }
      if (showImagesButton.value) {
        const videoCount = props.images.some((image) => image.type === "video") ? 1 : 0;
        const elevatedSpecsCount = isMediaCenterSection ? 1 : 0;
        return desktopImages.slice(0, 7 - videoCount + elevatedSpecsCount);
      }
      return desktopImages;
    });
    const showStaffPicksBadge = computed(() => props.staffTestimonialExperience !== EStaffTestimonialVariants.CONTROL);
    function onExpandMoreImages() {
      buttonClicked.value = true;
      emit("metricsClick", {
        linkName: "product images view all"
      });
    }
    function clickLeft() {
      emit("prevImage");
      emit("metricsClick", {
        linkName: "product image arrow click"
      });
    }
    function clickRight() {
      emit("nextImage");
      emit("metricsClick", {
        linkName: "product image arrow click"
      });
    }
    function isStandardOrUgcImage(image) {
      return image.type !== EImageType.VIDEO;
    }
    function isStandardImage(image) {
      return image.type !== EImageType.UGC && image.type !== EImageType.VIDEO;
    }
    function isProductImage(image) {
      return image.type === EImageType.PRODUCT || image.type === EImageType.ON_BODY || image.type === EImageType.DETAIL;
    }
    function getHeroClass(image) {
      return image.maxWidth > image.maxHeight ? "media-center-grid__hero--landscape" : "media-center-grid__hero--portrait";
    }
    function mediaCenterGridDesktopClass(image) {
      if (image.type === EImageType.ELEVATED_SPEC) return "media-center-grid__elevated-specs";
      return [image.src ? "media-center-grid__video" : "media-center-grid__image", {
        "media-center-grid__image-product": isProductImage(image)
      }, {
        "media-center-grid__image-ugc": image.type === "UGC Image"
      }, {
        "media-center-grid__image-swatch": image.type === "Swatch Image"
      }, {
        "media-center-grid__image--zoomable": isStandardOrUgcImage(image)
      }];
    }
    function productImageAspectRatio(image) {
      return isProductImage(image) ? {
        "aspect-ratio": `${image.maxWidth} / ${image.maxHeight}`
      } : void 0;
    }
    function handleImageZoomClick(image, index) {
      var _a;
      if (image.type === EImageType.ELEVATED_SPEC) return;
      if (isStandardOrUgcImage(image)) {
        emit("imageSelected", {
          image,
          caption: `${index + 1} of ${(_a = props.images) == null ? void 0 : _a.length}`
        });
        if (isStandardImage(image)) {
          emit("heroClick", imageRefs.value[index]);
        }
      }
    }
    function getComponentType(image) {
      if (image.type === EImageType.ELEVATED_SPEC) return ElevatedSpecs;
      return image.type === EImageType.UGC ? UgcImage : GenericMedia;
    }
    function getComponentProps(image) {
      var _a;
      if (image.type === EImageType.TESTIMONIAL) return props.staffTestimonialProps;
      let componentProps = {
        title: image.title ?? "",
        imageSrc: image.uri,
        xlImageSrc: image.xlBreakpointUri
      };
      if (image.type === EImageType.UGC) {
        componentProps = {
          ...componentProps,
          id: image.id,
          currentProductId: image.currentProductId,
          products: image.products,
          user: image.user,
          likes: image.likes,
          postedDate: image.postedDate,
          layout: "vertical",
          isFocusable: true,
          imgHeight: "1080",
          imgWidth: "1080"
        };
      } else {
        const shouldPad = image.type !== EImageType.ON_BODY && image.type !== EImageType.SWATCH && image.type !== EImageType.INFOGRAPHIC;
        componentProps = {
          ...componentProps,
          imageCaption: image.caption ?? "",
          videoLinkName: (_a = props.heroImageProps) == null ? void 0 : _a.videoLinkName,
          videoSrc: image.src,
          shouldPad,
          role: image.type === EImageType.VIDEO ? null : "button",
          tabIndex: image.type === EImageType.VIDEO ? -1 : 0
        };
      }
      return componentProps;
    }
    function getLazyOpts(image) {
      return {
        intersectionRatio: 1e-3,
        observerOpts: {
          // experiment analytics tag requires user to see the UGC image for a tag to fire
          rootMargin: image.type === EImageType.UGC ? "0px" : "500px",
          threshold: [1e-3, 0.1, 0.2, 0.5, 0.7]
        }
      };
    }
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1, [createElementVNode("div", _hoisted_2, [createElementVNode("div", _hoisted_3, [showStaffPicksBadge.value ? (openBlock(), createBlock(unref(StaffPickBadge), {
        key: 0
      })) : createCommentVNode("", true)]), _ctx.showArrows ? (openBlock(), createBlock(unref(CdrButton), {
        key: 0,
        ref: "leftArrow",
        "aria-label": "Show previous image",
        class: "media-center-grid__button media-center-grid__button--left",
        "icon-only": true,
        "with-background": true,
        onClick: clickLeft
      }, {
        default: withCtx(() => [createVNode(unref(IconArrowLeft))]),
        _: 1
      }, 512)) : createCommentVNode("", true), _ctx.showArrows ? (openBlock(), createBlock(unref(CdrButton), {
        key: 1,
        ref: "rightArrow",
        "aria-label": "Show next image",
        class: "media-center-grid__button media-center-grid__button--right",
        "icon-only": true,
        "with-background": true,
        onClick: clickRight
      }, {
        default: withCtx(() => [createVNode(unref(IconArrowRight))]),
        _: 1
      }, 512)) : createCommentVNode("", true), createVNode(unref(MediaCenterHeroImage), mergeProps({
        class: "media-center-grid__hero-image"
      }, _ctx.heroImageProps, {
        images: _ctx.images,
        "has-focusable-image": true,
        "staff-testimonial-props": _ctx.staffTestimonialProps,
        onImageSelected: _cache[0] || (_cache[0] = (img) => _ctx.$emit("imageSelected", img)),
        onHeroClick: _cache[1] || (_cache[1] = (data) => _ctx.$emit("heroClick", data)),
        onMetricsClick: _cache[2] || (_cache[2] = (data) => _ctx.$emit("metricsClick", data)),
        onImageSwipe: _cache[3] || (_cache[3] = ($event) => _ctx.$emit("metricsClick", {
          linkName: "product image swipe"
        })),
        onUgcImageClick: _cache[4] || (_cache[4] = (data) => _ctx.$emit("ugcImageClick", data))
      }), null, 16, ["images", "staff-testimonial-props"]), showElevatedSpecsMobile.value ? (openBlock(), createBlock(unref(ElevatedSpecs), {
        key: 2,
        class: "media-center-grid__elevated-specs--mobile",
        "data-ui": "elevated-specs-mobile",
        specs: _ctx.elevatedSpecs,
        type: unref(EElevatedSpecsCase).MEDIA_CENTER_MOBILE
      }, null, 8, ["specs", "type"])) : createCommentVNode("", true)]), showStaffPicksBadge.value ? (openBlock(), createBlock(unref(StaffPickBadge), {
        key: 0,
        class: "media-center-grid__staff-pick-container--desktop"
      })) : createCommentVNode("", true), createElementVNode("div", {
        class: normalizeClass(["media-center-grid--desktop", {
          "media-center-grid--desktop-xl": _ctx.showXlBreakpoint,
          "media-center-grid--less-images": showImagesButton.value
        }])
      }, [(openBlock(true), createElementBlock(Fragment, null, renderList(filteredImages.value, (image, index) => {
        return openBlock(), createElementBlock(Fragment, {
          key: image.id
        }, [image.type !== unref(EImageType).TESTIMONIAL ? (openBlock(), createBlock(resolveDynamicComponent(getComponentType(image)), mergeProps({
          key: 0,
          ref_for: true,
          ref_key: "imageRefs",
          ref: imageRefs,
          "data-ui": image.type === unref(EImageType).ELEVATED_SPEC ? "elevated-specs-section" : "media-center-image",
          "aria-label": "View larger image",
          "aria-haspopup": "dialog",
          "data-media-type": image.type,
          lazy: index > 1 ? true : false,
          "lazy-opts": getLazyOpts(image),
          class: [mediaCenterGridDesktopClass(image), index === 0 ? getHeroClass(image) : null],
          style: index !== 0 && productImageAspectRatio(image),
          "show-caption": true
        }, getComponentProps(image), {
          "show-xl-breakpoint": _ctx.showXlBreakpoint,
          "is-hero": index === 0,
          specs: _ctx.elevatedSpecs,
          type: elevatedSpecsDesktopType.value,
          "show-elevated-specs": index === 0 && isElevatedSpecsDesktopOverlay.value,
          "elevated-specs": _ctx.elevatedSpecs,
          onKeydown: [withKeys(($event) => handleImageZoomClick(image, index), ["enter"]), _cache[5] || (_cache[5] = withKeys(withModifiers(() => {
          }, ["prevent"]), ["space"]))],
          onKeyup: withKeys(($event) => handleImageZoomClick(image, index), ["space"]),
          onClick: ($event) => handleImageZoomClick(image, index),
          onUgcImageClick: _cache[6] || (_cache[6] = (data) => _ctx.$emit("ugcImageClick", data)),
          onMetricsClick: _cache[7] || (_cache[7] = (data) => _ctx.$emit("metricsClick", data))
        }), null, 16, ["data-ui", "data-media-type", "lazy", "lazy-opts", "class", "style", "show-xl-breakpoint", "is-hero", "specs", "type", "show-elevated-specs", "elevated-specs", "onKeydown", "onKeyup", "onClick"])) : createCommentVNode("", true)], 64);
      }), 128))], 2), showImagesButton.value ? (openBlock(), createElementBlock("button", {
        key: 1,
        "data-ui": "more-images-button",
        class: "media-center-grid__more-images-button",
        onClick: onExpandMoreImages
      }, [createTextVNode(toDisplayString(`View All ${_ctx.images.length} Images`) + " ", 1), createVNode(unref(IconCaretDown))])) : createCommentVNode("", true)]);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=MediaCenterGrid2.mjs.map
